import { AlertTriangle, ArrowUpRight, Bot, Check, ChevronDown, Copy, Info, LoaderCircle, LockKeyhole, Network, Radio, ShieldCheck, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { statusTone } from "./api";
import { providerFromString, providerGlyphs, providerLabel } from "./brands";

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand ${compact ? "brand--compact" : ""}`}>
      <svg className="brand__mark" viewBox="0 0 40 28" aria-hidden="true">
        <path d="M8 7.5h13.5c6.5 0 10.5 2.1 10.5 6.5s-4 6.5-10.5 6.5H8" />
        <path d="M32 7.5H18.5C12 7.5 8 9.6 8 14s4 6.5 10.5 6.5H32" />
        <circle cx="7" cy="7.5" r="2" />
        <circle cx="33" cy="20.5" r="2" />
      </svg>
      {!compact && <span>LoopGroup</span>}
    </span>
  );
}

/**
 * The agent a loop runs on, shown as that agent's own mark. Unknown agents get a generic bot;
 * loops declared by hand (no source) get the network glyph.
 */
export function ProviderMark({ provider, size = 34, className = "" }: { provider: string | null | undefined; size?: number; className?: string }) {
  const id = providerFromString(provider);
  const glyph = id && id !== "other" ? providerGlyphs[id] : null;
  return (
    <span className={`provider-mark provider-mark--${id ?? "none"} ${className}`.trim()} style={{ width: size, height: size }} role="img" aria-label={id ? `${providerLabel(id)} agent` : "Declared manually"}>
      {glyph ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d={glyph.path} fill="currentColor" fillRule="evenodd" clipRule="evenodd" /></svg> : id === "other" ? <Bot aria-hidden="true" /> : <Network aria-hidden="true" />}
    </span>
  );
}

export function Button({ className = "", variant = "primary", busy, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; busy?: boolean }) {
  return (
    <button className={`button button--${variant} ${className}`.trim()} {...props} disabled={busy || props.disabled} aria-busy={busy || undefined}>
      {busy ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : children}
    </button>
  );
}

export function IconButton({ label, className = "", children, ref, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; ref?: Ref<HTMLButtonElement> }) {
  return <button ref={ref} className={`icon-button ${className}`.trim()} {...props} aria-label={label}>{children}</button>;
}

export function TextLink({ className = "", children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={`text-link ${className}`.trim()} {...props}>{children}</a>;
}

export function Status({ value }: { value: string | null | undefined }) {
  return (
    <span className={`status status--${statusTone(value)}`}>
      <span className="status__dot" />
      {value || "unobserved"}
    </span>
  );
}

export type EvidenceStage = "cataloged" | "connected" | "reporting" | "evaluable" | "inferred" | "confirmed";

export function EvidenceChip({ stage, children }: { stage: EvidenceStage; children?: ReactNode }) {
  return <span className={`evidence-chip evidence-chip--${stage}`}><span aria-hidden="true" />{children ?? stage}</span>;
}

export function EmptyState({ eyebrow, title, children, action }: { eyebrow: string; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-state__orbit"><span /><span /><span /></div>
      <p className="eyebrow">{eyebrow}</p>
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Modal({ open, title, children, onClose }: { open: boolean; title: string; children: ReactNode; onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handle);
    return () => {
      window.removeEventListener("keydown", handle);
      previousFocus?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="modal__head"><h2 id={titleId}>{title}</h2><IconButton ref={closeRef} onClick={onClose} label="Close"><X size={18} aria-hidden="true" /></IconButton></div>
        {children}
      </section>
    </div>
  );
}

export function CopyButton({ value, label = "Copy", variant = "secondary", className }: { value: string; label?: string; variant?: "primary" | "secondary" | "ghost"; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant={variant}
      className={className}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1800);
        });
      }}
    >
      {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : label}
    </Button>
  );
}

export function SelectChevron() {
  return <ChevronDown className="select-chevron" size={14} aria-hidden="true" />;
}

export function ExternalArrow() {
  return <ArrowUpRight size={15} aria-hidden="true" />;
}

export function Field({ label, description, error, children, className = "" }: { label: string; description?: string; error?: string; children: ReactNode; className?: string }) {
  return <label className={`field ${error ? "field--error" : ""} ${className}`.trim()}><span>{label}</span>{description && <small>{description}</small>}{children}{error && <em role="alert">{error}</em>}</label>;
}

export function ScopeChoice({ name, label, detail, checked, disabled, onChange }: { name: string; label: string; detail: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return <label className={`scope-choice ${checked ? "scope-choice--selected" : ""}`}><input type="checkbox" name={name} checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><span><b>{label}</b><small>{detail}</small></span>{checked && <Check size={15} aria-hidden="true" />}</label>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow: string; title: string; description?: ReactNode; actions?: ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{description && <p>{description}</p>}</div>{actions && <div>{actions}</div>}</header>;
}

export function Panel({ eyebrow, title, action, children, className = "", surface = "paper" }: { eyebrow?: string; title?: string; action?: ReactNode; children: ReactNode; className?: string; surface?: "paper" | "receiver" }) {
  return <section className={`panel ${surface === "receiver" ? "receiver" : ""} ${className}`.trim()}>{(eyebrow || title || action) && <header className="panel__head"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}{title && <h2>{title}</h2>}</div>{action}</header>}{children}</section>;
}

export function LoadingState({ children = "Reading the operating picture…" }: { children?: ReactNode }) {
  return <div className="state-message" role="status"><LoaderCircle className="spin" size={20} aria-hidden="true" /><p>{children}</p></div>;
}

export function ErrorState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="state-message state-message--error" role="alert"><AlertTriangle size={20} aria-hidden="true" /><p>{children}</p>{action}</div>;
}

export type MetricState = "value" | "withheld" | "unknown";

export function MetricBlock({ label, value, evidence, state = "value", icon }: { label: string; value?: ReactNode; evidence: ReactNode; state?: MetricState; icon?: ReactNode }) {
  const shownValue = state === "value" ? value : "—";
  return <article className={`metric-block metric-block--${state}`}><div><span>{label}</span>{icon}</div><strong>{shownValue}</strong><small>{evidence}</small></article>;
}

export function CoverageBar({ label, value, detail }: { label: string; value: number | null; detail: ReactNode }) {
  const boundedValue = value == null ? 0 : Math.max(0, Math.min(100, value));
  return <div className="coverage-bar"><div><span>{label}</span><strong>{value == null ? "—" : `${boundedValue}%`}</strong></div><div className="coverage-bar__track" aria-hidden="true"><i style={{ width: `${boundedValue}%` }} /></div><small>{detail}</small></div>;
}

export interface RhythmPoint { label: string; succeeded: number; failed: number; unresolved: number }

export function RhythmChart({ points, summary }: { points: RhythmPoint[]; summary: string }) {
  const highest = Math.max(1, ...points.map((point) => point.succeeded + point.failed + point.unresolved));
  return <figure className="rhythm-chart rhythm-chart--shared"><figcaption className="sr-only">{summary}</figcaption><ol>{points.map((point) => { const total = point.succeeded + point.failed + point.unresolved; return <li key={point.label}><div className="rhythm-chart__plot"><span className="rhythm-chart__total">{total}</span><span className="rhythm-chart__stack" role="img" aria-label={`${point.label}: ${point.succeeded} succeeded, ${point.failed} failed, ${point.unresolved} unresolved`}>{point.succeeded > 0 && <i className="is-success" style={{ height: `${(point.succeeded / highest) * 100}%` }} />}{point.failed > 0 && <i className="is-failed" style={{ height: `${(point.failed / highest) * 100}%` }} />}{point.unresolved > 0 && <i className="is-neutral" style={{ height: `${(point.unresolved / highest) * 100}%` }} />}{total === 0 && <i className="is-zero" />}</span></div><time>{point.label}</time></li>; })}</ol></figure>;
}

function LedgerRow({ kind, icon, mark, title, meta, detail, trailing, className = "" }: { kind: "loop" | "diagnostic" | "activity"; icon?: ReactNode; mark?: ReactNode; title: ReactNode; meta?: ReactNode; detail?: ReactNode; trailing?: ReactNode; className?: string }) {
  return <article className={`ledger-row ledger-row--${kind} ${className}`.trim()}>{mark ?? (icon && <span className="ledger-row__icon">{icon}</span>)}<div><strong>{title}</strong>{meta && <small>{meta}</small>}{detail && <p>{detail}</p>}</div>{trailing && <aside>{trailing}</aside>}</article>;
}

export function LoopRow(props: Omit<Parameters<typeof LedgerRow>[0], "kind">) { return <LedgerRow kind="loop" {...props} />; }
export function DiagnosticRow(props: Omit<Parameters<typeof LedgerRow>[0], "kind">) { return <LedgerRow kind="diagnostic" {...props} />; }
export function ActivityRow(props: Omit<Parameters<typeof LedgerRow>[0], "kind">) { return <LedgerRow kind="activity" {...props} />; }

export function ReceiverPromptCard({ title, metadata, scopeLabel, children, actions, boundary }: { title: string; metadata: ReactNode; scopeLabel: string; children: ReactNode; actions?: ReactNode; boundary?: ReactNode }) {
  return <section className="receiver-prompt receiver"><header><span><Radio size={14} aria-hidden="true" />{scopeLabel}</span><small>{metadata}</small></header><div><h2>{title}</h2>{children}</div>{boundary && <aside><LockKeyhole size={15} aria-hidden="true" />{boundary}</aside>}{actions && <footer>{actions}</footer>}</section>;
}

export function BoundaryCallout({ title, children, tone = "private" }: { title: string; children: ReactNode; tone?: "private" | "shared" | "info" }) {
  const Icon = tone === "shared" ? ShieldCheck : tone === "info" ? Info : LockKeyhole;
  return <aside className={`boundary-callout boundary-callout--${tone}`}><Icon size={16} aria-hidden="true" /><div><strong>{title}</strong><p>{children}</p></div></aside>;
}

export function SharingGrant({ origin, scope, shared, children, className = "", ...props }: HTMLAttributes<HTMLDivElement> & { origin: string; scope: string; shared: boolean; children?: ReactNode }) {
  return <div className={`sharing-grant ${shared ? "sharing-grant--active" : ""} ${className}`.trim()} {...props}><span><ShieldCheck size={14} aria-hidden="true" />{shared ? "Shared" : "Private"}</span><strong>{scope}</strong><small>Origin · {origin}</small>{children}</div>;
}
